import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { AlphabeticalNav } from '@sky-it-elections/design-system';

const PREFIX_DS = 'ele__';
const MUNICIPALITY_LIST_ID_SELECTOR = `${PREFIX_DS}list-container`;
const MUNICIPALITY_LIST_AEM_SELECTOR = `${PREFIX_DS}aem-list-container`;
const MUNICIPALITY_LIST_HIDDEN_SELECTOR = `${PREFIX_DS}municipality-list--hidden`;
const DEFAULT_SELECTED_LETTER = 'A';
const DESKTOP_BREAKPOINT = 1024;
const LOCAL_NAV_HEIGHT_DESKTOP = 66; // Navbar height on desktop devices
const LOCAL_NAV_HEIGHT_MOBILE = 60; // Navbar height on mobile devices
const ALPHABETICAL_NAV_HEIGHT = 56;

interface IInnerHTML {
  __html: string;
}

const getInitialLetterList = (municipalityListBlocksContainer: HTMLElement) => {
  return Array.from(municipalityListBlocksContainer.querySelectorAll('ul')).reduce(
    (acc, municipalityListBlock: HTMLElement) =>
      municipalityListBlock.dataset.letter ? [...acc, municipalityListBlock.dataset.letter] : acc,
    []
  );
};

const buildHtmlCode = (children: ReactNode): IInnerHTML => ({ __html: String(children) });

const MunicipalityListWrapper: FC = ({ children }) => {
  const [municipalityListBlockContainer, setMunicipalityListBlockContainer] = useState<HTMLElement>();
  const [currentLetter, setCurrentLetter] = useState<string>(DEFAULT_SELECTED_LETTER);
  const [initialLetterList, setInitialLetterList] = useState<string[]>([]);

  // AEM HTML LIST WRAPPER
  const refComponent = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (refComponent.current) {
      setMunicipalityListBlockContainer(refComponent.current);
      setInitialLetterList(getInitialLetterList(refComponent.current));
    }
  }, []);

  // Set CURRENT LETTER in ALPHABETICAL NAV & show ALL MUNICIPALIEITES starting with CURRENT LETTER
  useEffect(() => {
    initialLetterList.length && setCurrentLetter(initialLetterList[0] ?? DEFAULT_SELECTED_LETTER);

    const municipalityListBlock = municipalityListBlockContainer?.querySelector(
      `ul[data-letter="${initialLetterList[0]}"]`
    );
    municipalityListBlock && municipalityListBlock?.classList.remove(MUNICIPALITY_LIST_HIDDEN_SELECTOR);
  }, [initialLetterList, municipalityListBlockContainer]);

  const scrollToTop = (letter: string) => {
    if (refComponent.current) {
      const localNavHeight =
        window && window.innerWidth > DESKTOP_BREAKPOINT ? LOCAL_NAV_HEIGHT_DESKTOP : LOCAL_NAV_HEIGHT_MOBILE;
      const listContainer = refComponent.current.querySelector(`#${MUNICIPALITY_LIST_AEM_SELECTOR}`);
      setCurrentLetter(letter);

      const municipalityListAllBlocks = municipalityListBlockContainer?.querySelectorAll(
        `ul` // TODO: unify in useEffect
      );

      const municipalityListBlock = municipalityListBlockContainer?.querySelector(`ul[data-letter="${letter}"]`); // TODO: unify in useEffect

      municipalityListAllBlocks?.forEach((municipalityListAllBlock) => {
        municipalityListAllBlock.classList.add(MUNICIPALITY_LIST_HIDDEN_SELECTOR);
      });

      municipalityListBlock?.classList.remove(MUNICIPALITY_LIST_HIDDEN_SELECTOR);

      const navbarsCumulativeHeight = localNavHeight + ALPHABETICAL_NAV_HEIGHT;

      listContainer &&
        listContainer.getBoundingClientRect().top < navbarsCumulativeHeight &&
        window &&
        window.scroll({
          top:
            window.pageYOffset +
            (listContainer ? listContainer.getBoundingClientRect().top : 0) -
            navbarsCumulativeHeight,
          left: 0,
          behavior: 'auto',
        });
    }
  };

  return (
    <React.Fragment>
      <AlphabeticalNav
        wordList={initialLetterList}
        currentLetter={currentLetter}
        onLetterClick={(letter: string) => scrollToTop(letter)}
      />
      <div id={MUNICIPALITY_LIST_ID_SELECTOR} ref={refComponent} dangerouslySetInnerHTML={buildHtmlCode(children)} />
    </React.Fragment>
  );
};

export default MunicipalityListWrapper;
